import { ReactElement } from 'react';
import Image from 'next/image';
import { useAPI } from '@/services';
import { TCardBin } from '@/services/apis/cardTransfer/schemas';

interface IReturnBankLogoOptions {
  input: string;
  width: number;
  height: number;
}

interface IUseMelliCardBankData {
  isCardAlreadySaved: (__destination: string) => boolean;
  findBankBin: (__bin: string) => TCardBin | null;
  isLoading: boolean;
  returnBankLogo: (__options: IReturnBankLogoOptions) => ReactElement | null;
}

export const useMelliCardBankData = (): IUseMelliCardBankData => {
  const { data, isLoading } = useAPI(['fundTransfer', 'getSavedCards'], {
    reactQueryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  });

  const { data: bankBins } = useAPI(['cardTransfer', 'getCardBins'], {
    reactQueryOptions: { retry: false },
  });

  const findBankBin = (bin: string) => {
    const bank = bankBins?.bins?.find((item) => bin?.includes(item?.bin));
    return bank || null;
  };

  const isCardAlreadySaved = (destination: string) => {
    const savedCard = data?.cards?.find((item) => item?.pan === destination);
    return !!savedCard;
  };

  const returnBankLogo = ({ input, width, height }: IReturnBankLogoOptions) => {
    // Card Bin Number is the first 6 digits of the card number
    const inputBinNumber = input.slice(0, 6);

    // Bank Code is 3rd to 5th digits of Sheba number (without "IR" prefix)
    const inputBankCode = input.slice(2, 5);

    const bankData = bankBins?.bins?.find(
      (item) => item?.bin === inputBinNumber || item?.bankCode === inputBankCode
    );
    if (bankData)
      return (
        <Image
          alt={bankData?.titleFa}
          height={height}
          src={bankData?.logo}
          width={width}
        />
      );
    return null;
  };

  return {
    isCardAlreadySaved,
    isLoading,
    findBankBin,
    returnBankLogo,
  };
};
